import {
  formatHref,
  formatQueryParameters,
  isStageEnv,
  parseJwt,
  parseQueryParameter,
  utcTimestamp,
} from '@fulcrum/lib';
import { SessionObserverManager } from './observers';
import { getSessionStorageKey } from './storage/standard-keys';
import {
  IAuthGrants,
  IConfiguration,
  IIdentityToken,
  IGetSessionOptions,
  IRedirect,
  ISession,
  ISessionEvent,
  IStorageAdapter,
  IUser,
  RefreshFailureType,
  SessionEventType,
  SessionObserver,
  SessionStorageTarget,
} from './types';
import { PKCEManager, createPKCEManager } from './pkce';
import {
  isPkceRefreshDebugEnabled,
  logPkceRefresh,
  logPkceRefreshFailure,
  maskSecret,
  parseOAuthErrorBody,
} from './pkce-refresh-debug';
import { createStorageAdapterFromHelper } from './storage-adapter';
import { StorageLock } from './storage-lock';
import { sleep } from '@trimble/http-utils';
import { waitForCondition } from './session-wait-utils';

/**
 * Thrown by `signInSilently` when the IdP returns an error that requires
 * interactive user action rather than just a retry.
 *
 * `login_required`  — the IdP SSO session / cookie has expired. The user must
 *                     authenticate again. A popup or page redirect is needed.
 * `consent_required`— the user has not yet granted consent to this client.
 *                     An interactive authorization must be initiated.
 * `interaction_required` — a catch-all that maps to the same need for user
 *                     interaction as defined in RFC 6749 / OIDC spec.
 */
export class SilentAuthRequiresInteractionError extends Error {
  /** The raw OAuth error code returned by the IdP. */
  public readonly errorCode: string;

  constructor(errorCode: string, message?: string) {
    super(message ?? `Silent sign-in failed: ${errorCode}`);
    this.name = 'SilentAuthRequiresInteractionError';
    this.errorCode = errorCode;
  }

  /** Returns true when the IdP SSO session / cookie has expired. */
  public get isSsoExpired(): boolean {
    return this.errorCode === 'login_required';
  }

  /** Returns true when explicit user consent is required. */
  public get isConsentRequired(): boolean {
    return this.errorCode === 'consent_required';
  }
}

/** OAuth error codes that require interactive user action (cannot be retried silently). */
const INTERACTION_REQUIRED_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'account_selection_required',
]);

/** Callback type for checking if current tab is the master tab */
export type MasterCheckCallback = () => boolean;

/** Token refresh lock name */
const TOKEN_REFRESH_LOCK_NAME = 'token_refresh';

/**
 * Max wait for non-master tabs to receive a refreshed session from the master tab.
 *
 * Matches legacy tcweb `initTIDCredentials` non-master fallback (50s). Master refresh
 * can span retries with exponential backoff; a shorter window caused non-master tabs to
 * return stale tokens and trigger duplicate refresh / 401 storms. Trade-off: when the
 * master tab is gone, `getSession(true)` polls for up to this duration before returning
 * the last known session via `SESSION_REFRESH_SKIPPED`.
 */
const NON_MASTER_REFRESH_WAIT_MS = 50000;

/** Retry configuration */
const REFRESH_RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
};

/** HTTP status codes that indicate permanent failures (no retry) */
const PERMANENT_FAILURE_CODES = [400, 401, 403];

/** Error messages that indicate permanent failures */
const PERMANENT_FAILURE_MESSAGES = [
  'invalid_grant',
  'invalid_request',
  'invalid_token',
  'invalid refresh token',
  'token_expired',
  'unauthorized',
];

/**
 * Trimble Identity Service
 * Provides OAuth2/OIDC authentication and session management with observer pattern support
 */
export class TrimbleIdentityService {
  private readonly config: IConfiguration = {
    connectProvider: '',
    identityClientApp: '',
    identityClientId: '',
    identityClientKey: '',
    identityProvider: '',
    identityRedirectUri: '',
  };

  private readonly storageAdapter: IStorageAdapter;
  private readonly sessionStorageAdapter: IStorageAdapter | null;
  private sessionStorageTarget: SessionStorageTarget = 'LOCAL';
  private readonly observerManager: SessionObserverManager =
    new SessionObserverManager();
  private readonly sessionKey: string[];
  private readonly pkceManager: PKCEManager;
  private readonly refreshLock: StorageLock;
  private masterCheckCallback: MasterCheckCallback | null = null;
  private readonly memoryOnly: boolean;

  /**
   * Creates a new instance of TrimbleIdentityService
   * @param storageAdapter Storage adapter for session persistence
   * @param sessionKey Optional session key array (defaults based on environment using standard keys)
   * @param pkceManager Optional PKCE manager (will be created automatically if not provided)
   * @param memoryOnly If true, operates in memory-only mode (no multi-tab coordination, no locks)
   */
  constructor(
    storageAdapter: IStorageAdapter,
    sessionKey?: string[],
    pkceManager?: PKCEManager,
    memoryOnly = false
  ) {
    this.memoryOnly = memoryOnly;
    this.storageAdapter = storageAdapter;
    this.sessionStorageAdapter = memoryOnly
      ? null
      : createStorageAdapterFromHelper(true);
    this.sessionKey =
      sessionKey || getSessionStorageKey(isStageEnv(window.location.href));
    // PKCE verifiers must survive the OAuth redirect reload. In memory-only
    // mode the session itself stays in the caller's adapter, but PKCE still
    // uses sessionStorage so signIn() can exchange the authorization code.
    const pkceStorageAdapter = memoryOnly
      ? createStorageAdapterFromHelper(true)
      : storageAdapter;
    this.pkceManager =
      pkceManager || createPKCEManager(pkceStorageAdapter, memoryOnly);
    this.refreshLock = new StorageLock({
      lockTimeout: 30000,
      maxWaitTime: 35000,
    });

    if (memoryOnly) {
      this.masterCheckCallback = null;
    }
  }

  /**
   * Sets the master check callback for multi-tab coordination.
   * When set, token refresh operations will only be performed by the master tab.
   * Non-master tabs will return the current session without refreshing.
   *
   * @param callback Function that returns true if this tab is the master tab
   */
  public setMasterCheckCallback(callback: MasterCheckCallback | null): void {
    if (!this.memoryOnly) {
      this.masterCheckCallback = callback;
    }
  }

  /**
   * Checks if this tab is the master tab (if master check is configured).
   * Returns true if no master check is configured (single-tab mode).
   * Always returns true in memory-only mode (multi-tab coordination disabled).
   */
  public isMasterTab(): boolean {
    if (this.memoryOnly) {
      return true;
    }
    return this.masterCheckCallback ? this.masterCheckCallback() : true;
  }

  private classifyRefreshError(
    error: Error,
    statusCode?: number
  ): RefreshFailureType {
    if (statusCode && PERMANENT_FAILURE_CODES.includes(statusCode)) {
      return 'permanent';
    }

    const errorMessage = error.message.toLowerCase();
    for (const indicator of PERMANENT_FAILURE_MESSAGES) {
      if (errorMessage.includes(indicator)) {
        return 'permanent';
      }
    }

    return 'temporary';
  }

  private calculateRetryDelay(attempt: number): number {
    const delay =
      REFRESH_RETRY_CONFIG.initialDelayMs *
      Math.pow(REFRESH_RETRY_CONFIG.backoffMultiplier, attempt);
    // eslint-disable-next-line sonarjs/pseudo-random -- retry jitter only, not security-sensitive
    const jitter = delay * 0.2 * (Math.random() * 2 - 1);
    return Math.min(delay + jitter, REFRESH_RETRY_CONFIG.maxDelayMs);
  }

  /**
   * Subscribe to session events
   * @param observer Callback function to be called on session events
   * @returns Unsubscribe function
   */
  public subscribe(observer: SessionObserver): () => void {
    return this.observerManager.subscribe(observer);
  }

  /**
   * Unsubscribe from session events
   * @param observer Observer to remove
   */
  public unsubscribe(observer: SessionObserver): void {
    this.observerManager.unsubscribe(observer);
  }

  /**
   * Get current configuration
   */
  public getConfig(): IConfiguration {
    return { ...this.config };
  }

  /**
   * Configures the identity provider. You MUST call this before using any other function.
   * @param configuration The configuration.
   */
  public async configure(configuration: IConfiguration): Promise<void> {
    if (!this.isValidConfiguration(configuration)) {
      throw new Error('Invalid configuration.');
    }

    Object.assign(this.config, configuration);

    if (
      configuration.memoryOnly !== undefined &&
      configuration.memoryOnly !== this.memoryOnly
    ) {
      console.warn(
        'memoryOnly in configuration differs from constructor value. Using constructor value.'
      );
    }

    if (this.config.authGrants) {
      await this.saveSession(this.convertToSession(this.config.authGrants));
    }

    this.notifyObservers({
      type: SessionEventType.CONFIGURATION_CHANGED,
      configuration: this.config,
    });
  }

  /**
   * Revokes the access token.
   * TID requires code_verifier + client_id for revocation.
   */
  public async revokeAccessToken(): Promise<IRedirect> {
    this.ensureConfigured();
    const session = await this.currentSession();

    if (this.isValidSession(session)) {
      const [codeVerifier] = await this.pkceManager.getCodeVerifierAsync();
      await this.revokeToken(
        session.accessToken!,
        'access_token',
        codeVerifier
      );
    }

    return { uri: this.getSignInHref() };
  }

  /**
   * Revokes the refresh token.
   * TID requires code_verifier + client_id for revocation.
   */
  public async revokeRefreshToken(): Promise<void> {
    this.ensureConfigured();
    const session = await this.currentSession();

    if (session && session.refreshToken) {
      const [codeVerifier] = await this.pkceManager.getCodeVerifierAsync();
      await this.revokeToken(
        session.refreshToken,
        'refresh_token',
        codeVerifier
      );

      this.notifyObservers({
        type: SessionEventType.TOKEN_REVOKED,
        session,
      });
    }
  }

  /**
   * Removes the persisted session for this service without OIDC end-session redirect.
   */
  public async clearStoredSession(): Promise<void> {
    this.ensureConfigured();
    await this.clearSession();
  }

  /**
   * Begins or resumes a session. Returns redirect URI if session cannot be acquired.
   * PKCE code verifier and challenges are managed internally.
   * @param configuration The app session configuration.
   * @param sourceUrl The URL to parse the OAuth `code`/`state` from. Defaults to
   * the current browser location; pass an explicit URL to complete a callback
   * whose parameters are not on `window.location`.
   */
  public async signIn(
    configuration?: IConfiguration,
    sourceUrl: string = window.location.href
  ): Promise<ISession | IRedirect> {
    if (configuration) {
      await this.configure(configuration);
    }

    this.ensureConfigured();

    const code = parseQueryParameter(sourceUrl, 'code');

    if (code) {
      try {
        const pkce = await this.pkceManager.getCodeVerifierAndChallengesAsync();

        logPkceRefresh('sign-in authorization_code request', {
          codeVerifier: maskSecret(pkce.codeVerifier),
          codeChallenge: maskSecret(pkce.nextCodeChallenge),
          pairing: 'verifier + S256(NEXT)',
        });

        const session = await this.requestSession(
          code,
          pkce.codeVerifier,
          pkce.nextCodeChallenge
        );

        await this.pkceManager.updateCodeVerifierAsync();
        const pkceAfterSignIn =
          await this.pkceManager.getCodeVerifierAndChallengesAsync();
        logPkceRefresh('sign-in PKCE rotated (post authorization_code)', {
          codeVerifier: maskSecret(pkceAfterSignIn.codeVerifier),
          nextCodeVerifier: maskSecret(pkceAfterSignIn.nextCodeVerifier),
          codeChallenge: maskSecret(pkceAfterSignIn.codeChallenge),
        });
        await this.saveSession(session);

        this.notifyObservers({
          type: SessionEventType.SIGN_IN,
          session,
        });

        return {
          uri:
            parseQueryParameter(sourceUrl, 'state') ||
            this.config.identityRedirectUri,
        };
      } catch {
        const existingSession = await this.loadSession();
        if (!this.isValidSession(existingSession)) {
          await this.clearSession();
        }
        throw new Error('Failed to acquire the session');
      }
    }

    return this.getSession();
  }

  /**
   * Acquires a session silently via a hidden iframe using OAuth `prompt=none`.
   *
   * Intended for scenarios where the host application is already signed in
   * (SSO cookie present) and we need to obtain an additional session for a
   * different `client_id` (for example, per-extension sessions) without
   * redirecting the host page.
   *
   * The configured `identityRedirectUri` MUST point to a lightweight page
   * that posts a message of the form
   * `{ type: 'trimble-identity-silent-auth', code, state, error }` back to
   * the host page. Use `handleSilentAuthRedirectCallback()` (or
   * `postSilentAuthCallbackMessage`) so iframe and popup flows both work.
   *
   * **SSO-session-expired recovery**: if the IdP SSO cookie has expired (common
   * after the machine sleeps for hours), this method throws a
   * `SilentAuthRequiresInteractionError` (`.isSsoExpired === true`). Callers
   * should catch that type and either:
   *   1. Retry after supplying `opts.idTokenHint` from the host's current id_token
   *      (TID can re-establish the session from the hint), or
   *   2. Fall back to `signInWithPopup` to re-authenticate the user interactively
   *      without navigating away from the current page.
   *
   * @param opts Optional parameters
   * @param opts.iframeTimeoutMs Max time to wait for the iframe callback (default 10 000 ms)
   * @param opts.idTokenHint Previously issued id_token to help the IdP identify
   *   the user when its SSO cookie is absent. Passed as `id_token_hint` in the
   *   `/authorize` request. The token may be expired — TID uses only the `sub`
   *   claim as a user-identity hint.
   * @throws {SilentAuthRequiresInteractionError} when the IdP signals that user
   *   interaction is required (`login_required`, `consent_required`, etc.)
   * @returns Promise resolving to the acquired session
   */
  public async signInSilently(opts?: {
    iframeTimeoutMs?: number;
    idTokenHint?: string;
  }): Promise<ISession> {
    this.ensureConfigured();

    const timeoutMs = opts?.iframeTimeoutMs ?? 10000;
    const pkce = await this.pkceManager.getCodeVerifierAndChallengesAsync();
    const state = this.generateStateNonce();

    const authorizeParams: Record<string, string | undefined> = {
      client_id: this.config.identityClientId,
      redirect_uri: this.config.identityRedirectUri,
      response_type: 'code',
      scope: this.buildScope(),
      state,
      prompt: 'none',
      code_challenge: pkce.codeChallenge,
      code_challenge_method: 'S256',
    };

    if (opts?.idTokenHint) {
      authorizeParams['id_token_hint'] = opts.idTokenHint;
    }

    const authorizeUrl = formatHref(
      this.config.identityProvider,
      '/oauth/authorize',
      authorizeParams
    );

    const redirectOrigin = new URL(this.config.identityRedirectUri).origin;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('title', 'trimble-identity-silent-auth');

    const codePromise = new Promise<string>((resolve, reject) => {
      let settled = false;

      const cleanup = (): void => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutId);
      };

      const handleMessage = (event: MessageEvent): void => {
        if (settled) return;
        if (event.origin !== redirectOrigin) return;

        const data = event.data as
          | {
              type?: string;
              code?: string;
              state?: string;
              error?: string;
            }
          | undefined;

        if (!data || data.type !== 'trimble-identity-silent-auth') return;
        if (data.state !== state) return;

        settled = true;
        cleanup();

        if (data.error) {
          if (INTERACTION_REQUIRED_CODES.has(data.error)) {
            reject(new SilentAuthRequiresInteractionError(data.error));
          } else {
            reject(new Error(`Silent sign-in failed: ${data.error}`));
          }
          return;
        }
        if (!data.code) {
          reject(new Error('Silent sign-in failed: no authorization code'));
          return;
        }
        resolve(data.code);
      };

      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('Silent sign-in timed out'));
      }, timeoutMs);

      window.addEventListener('message', handleMessage);
    });

    iframe.src = authorizeUrl;
    document.body.appendChild(iframe);

    let code: string;
    try {
      code = await codePromise;
    } finally {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }

    const session = await this.requestSession(
      code,
      pkce.codeVerifier,
      pkce.nextCodeChallenge
    );
    await this.pkceManager.updateCodeVerifierAsync();
    await this.saveSession(session);

    this.notifyObservers({
      type: SessionEventType.SIGN_IN,
      session,
    });

    return session;
  }

  /**
   * Re-authenticates the user interactively via a popup window, without
   * navigating the current page away. Useful when `signInSilently` throws
   * a `SilentAuthRequiresInteractionError` (SSO cookie expired) and you need
   * to re-establish the IdP session without losing the user's current context.
   *
   * Opens a small popup pointing to the full `/authorize` URL (no `prompt`
   * restriction). The popup redirects to `identityRedirectUri` on success;
   * the callback page must post a `trimble-identity-silent-auth` message to
   * the opener via `handleSilentAuthRedirectCallback()` (or
   * `postSilentAuthCallbackMessage`) so this method can exchange the code.
   *
   * @param opts Optional parameters
   * @param opts.popupTimeoutMs Max time to wait for the popup callback (default 120 000 ms)
   * @param opts.popupWidth  Width of the popup window in pixels (default 520)
   * @param opts.popupHeight Height of the popup window in pixels (default 640)
   * @throws {Error} if the popup is blocked or the user closes it before completing
   * @returns Promise resolving to the acquired session
   */
  public async signInWithPopup(opts?: {
    popupTimeoutMs?: number;
    popupWidth?: number;
    popupHeight?: number;
  }): Promise<ISession> {
    this.ensureConfigured();

    const timeoutMs = opts?.popupTimeoutMs ?? 120_000;
    const popupWidth = opts?.popupWidth ?? 520;
    const popupHeight = opts?.popupHeight ?? 640;

    const pkce = await this.pkceManager.getCodeVerifierAndChallengesAsync();
    const state = this.generateStateNonce();

    const authorizeUrl = formatHref(
      this.config.identityProvider,
      '/oauth/authorize',
      {
        client_id: this.config.identityClientId,
        redirect_uri: this.config.identityRedirectUri,
        response_type: 'code',
        scope: this.buildScope(),
        state,
        code_challenge: pkce.codeChallenge,
        code_challenge_method: 'S256',
      }
    );

    const redirectOrigin = new URL(this.config.identityRedirectUri).origin;

    // Center the popup on screen.
    const left = Math.max(
      0,
      Math.round((window.screen.width - popupWidth) / 2)
    );
    const top = Math.max(
      0,
      Math.round((window.screen.height - popupHeight) / 2)
    );

    const popup = window.open(
      authorizeUrl,
      'trimble-identity-popup-auth',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},` +
        'toolbar=no,menubar=no,scrollbars=yes,resizable=yes,status=no'
    );

    if (!popup) {
      throw new Error(
        'signInWithPopup: popup was blocked. ' +
          'Call this method from a user-gesture handler (click, etc.) to avoid popup blockers.'
      );
    }

    const codePromise = new Promise<string>((resolve, reject) => {
      let settled = false;

      const cleanup = (): void => {
        window.removeEventListener('message', handleMessage);
        clearInterval(closedPollId);
        clearTimeout(timeoutId);
      };

      const handleMessage = (event: MessageEvent): void => {
        if (settled) return;
        if (event.origin !== redirectOrigin) return;

        const data = event.data as
          | { type?: string; code?: string; state?: string; error?: string }
          | undefined;

        if (!data || data.type !== 'trimble-identity-silent-auth') return;
        if (data.state !== state) return;

        settled = true;
        cleanup();
        try {
          popup.close();
        } catch {
          // ignore cross-origin close errors
        }

        if (data.error) {
          if (INTERACTION_REQUIRED_CODES.has(data.error)) {
            reject(new SilentAuthRequiresInteractionError(data.error));
          } else {
            reject(new Error(`Popup sign-in failed: ${data.error}`));
          }
          return;
        }
        if (!data.code) {
          reject(new Error('Popup sign-in failed: no authorization code'));
          return;
        }
        resolve(data.code);
      };

      // Detect if the user closed the popup without completing sign-in.
      const closedPollId = setInterval(() => {
        if (settled) return;
        if (popup.closed) {
          settled = true;
          cleanup();
          reject(new Error('Popup sign-in cancelled: user closed the window'));
        }
      }, 500);

      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        try {
          popup.close();
        } catch {
          // ignore
        }
        reject(new Error('Popup sign-in timed out'));
      }, timeoutMs);

      window.addEventListener('message', handleMessage);
    });

    const code = await codePromise;

    const session = await this.requestSession(
      code,
      pkce.codeVerifier,
      pkce.nextCodeChallenge
    );
    await this.pkceManager.updateCodeVerifierAsync();
    await this.saveSession(session);

    this.notifyObservers({
      type: SessionEventType.SIGN_IN,
      session,
    });

    return session;
  }

  /**
   * Ends the current session. Will redirect.
   */
  public async signOut(): Promise<IRedirect> {
    this.ensureConfigured();
    const previousSession = await this.currentSession();
    await this.clearSession();

    this.notifyObservers({
      type: SessionEventType.SIGN_OUT,
      previousSession,
    });

    if (previousSession && previousSession.sessionToken) {
      return { uri: this.getSignOutHref(previousSession) };
    }

    return { uri: this.getSignOutRedirectUrl() };
  }

  /**
   * Begins a session in automatic testing where authGrants have already been included into configuration.
   */
  public async useAuthGrants(configuration: IConfiguration): Promise<ISession> {
    if (configuration) {
      await this.configure(configuration);
    }

    this.ensureConfigured();

    const session = this.convertToSession(
      configuration.authGrants as IAuthGrants
    );
    await this.saveSession(session);

    this.notifyObservers({
      type: SessionEventType.SIGN_IN,
      session,
    });

    return session;
  }

  /**
   * Returns the saved session data from storage.
   */
  public async loadSession<T extends ISession = ISession>(): Promise<T> {
    return (await this.currentSession()) as T;
  }

  /**
   * Saves the session data to storage.
   * @param session The session.
   */
  public async saveSession<T extends ISession>(
    session: Partial<T>
  ): Promise<T> {
    const previousSession = await this.currentSession();
    const updatedSession = { ...previousSession, ...session } as T;

    await this.getSessionStorage().setItem(
      this.sessionKey,
      JSON.stringify(updatedSession)
    );

    this.notifyObservers({
      type: SessionEventType.SESSION_SAVED,
      session: updatedSession,
      previousSession,
    });

    return updatedSession;
  }

  /**
   * Sets the session storage target (LOCAL or SESSION storage)
   * @param target Storage target type
   */
  public setSessionStorage(target: SessionStorageTarget): void {
    this.sessionStorageTarget = target;
  }

  /**
   * Returns the current session or redirect URI if redirect is required.
   * PKCE code verifier and challenges are managed internally.
   *
   * When master check is configured (via setMasterCheckCallback):
   * - Only the master tab will perform token refresh
   * - Non-master tabs will return current session (relies on session broadcast for updates)
   *
   * Token refresh includes retry logic with exponential backoff:
   * - Temporary failures (network, 5xx) are retried up to 3 times
   * - Permanent failures (401, invalid_grant) clear session immediately
   *
   * @param refresh Indicates whether to force session refresh.
   * @param options Optional session retrieval options (e.g. bypass master-tab check).
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 18 > 15 allowed; refactor later
  public async getSession<T extends ISession = ISession>(
    refresh = false,
    options?: IGetSessionOptions
  ): Promise<T | IRedirect> {
    this.ensureConfigured();
    const session = await this.currentSession();
    const bypassMasterCheck = options?.bypassMasterCheck ?? false;

    if (!refresh && this.isValidSession(session)) {
      return session as T;
    }

    if (this.isValidSessionIssuer(session) && this.canRefresh(session)) {
      if (this.memoryOnly) {
        return this.performRefreshWithRetry<T>(session, refresh);
      }

      const shouldWaitForMaster = !this.isMasterTab() && !bypassMasterCheck;

      if (shouldWaitForMaster) {
        // Non-master tabs never refresh directly; wait for master broadcast/storage sync.
        // See NON_MASTER_REFRESH_WAIT_MS for the 50s ceiling and degraded-mode trade-off.
        const conditionMet = await waitForCondition(
          async () => {
            const currentSession = await this.currentSession();
            return this.isValidSession(currentSession);
          },
          { maxWaitTimeMs: NON_MASTER_REFRESH_WAIT_MS, checkIntervalMs: 100 }
        );

        if (conditionMet) {
          return (await this.currentSession()) as T;
        }

        this.notifyObservers({
          type: SessionEventType.SESSION_REFRESH_SKIPPED,
          session,
          reason: 'not_master_tab',
        });
        return session as T;
      }

      try {
        const refreshedSession = await this.refreshLock.withLock(
          TOKEN_REFRESH_LOCK_NAME,
          async () => {
            const sessionInLock = await this.currentSession();
            // Do not treat "still valid" as "already refreshed" for master/eager
            // getSession(true): a soon-to-expire token must still be rotated.
            // Follower fallback (bypassMasterCheck) and a token that another tab
            // already rotated may skip the network call.
            if (
              this.isValidSession(sessionInLock) &&
              (bypassMasterCheck ||
                sessionInLock.accessToken !== session.accessToken)
            ) {
              return sessionInLock as T;
            }

            return this.performRefreshWithRetry<T>(sessionInLock, refresh, {
              preferStoredValidSession: bypassMasterCheck,
            });
          }
        );
        return refreshedSession as T;
      } catch (error) {
        if (
          error instanceof Error &&
          error.message.includes('Failed to acquire lock')
        ) {
          return session as T;
        }

        throw error;
      }
    }

    const pkce = await this.pkceManager.getCodeVerifierAndChallengesAsync();

    return {
      uri: this.getSignInHref(undefined, pkce.codeChallenge),
    };
  }

  /**
   * Get sign out redirect URL
   */
  public getSignOutRedirectUrl(): string {
    return (
      this.config.identitySignOutRedirectUri ?? this.config.identityRedirectUri
    );
  }

  /**
   * Get sign in redirect URL with PKCE challenge.
   * @param states Optional state parameters to include in the redirect URL
   * @returns Promise resolving to the sign-in redirect URL with PKCE challenge
   */
  public async getSignInRedirectUrl(
    states?: Record<string, string>,
    rotatePkce = false
  ): Promise<string> {
    this.ensureConfigured();
    const pkce =
      await this.pkceManager.getCodeVerifierAndChallengesAsync(rotatePkce);
    return this.getSignInHref(states, pkce.codeChallenge);
  }

  /**
   * Get the session storage key being used
   */
  public getSessionKey(): string[] {
    return this.sessionKey;
  }

  // Private helper methods

  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 26 > 15 allowed; refactor later
  private async performRefreshWithRetry<T extends ISession>(
    session: ISession,
    forceRefresh = false,
    options?: { preferStoredValidSession?: boolean }
  ): Promise<T> {
    const { maxRetries } = REFRESH_RETRY_CONFIG;
    let lastError: Error | undefined;

    // QA branch / working INT pairing: current CODE_VERIFIER +
    // S256(NEXT_CODE_VERIFIER), same shape as sign-in. Rotate only after a
    // successful oauth/token (matches feat/Integration-Apps-Caps-separation-qa-common).
    const pkce = await this.pkceManager.getCodeVerifierAndChallengesAsync();
    logPkceRefresh('refresh starting', {
      forceRefresh,
      sessionKey: this.sessionKey.join('/'),
      refreshToken: maskSecret(session.refreshToken),
      pkce: {
        codeVerifier: maskSecret(pkce.codeVerifier),
        nextCodeVerifier: maskSecret(pkce.nextCodeVerifier),
        codeChallenge: maskSecret(pkce.codeChallenge),
        nextCodeChallenge: maskSecret(pkce.nextCodeChallenge),
      },
      expectedOnWire: {
        code_verifier: maskSecret(pkce.codeVerifier),
        code_challenge: maskSecret(pkce.nextCodeChallenge),
        pairing: 'CODE verifier + S256(NEXT); rotate after success',
      },
    });

    let lastRefreshSource = session;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Multi-tab race guard: on retry attempts (or non-forced refreshes),
        // re-check storage in case another tab has already refreshed and
        // broadcast a new valid session — if so, use that and skip the
        // network call. On attempt 0 of a forced refresh we intentionally
        // skip this check: the caller explicitly asked for a new token (for
        // example, the eager-refresh scheduler) and the session they just
        // loaded would otherwise short-circuit the whole refresh.
        // Degraded-mode follower fallback sets preferStoredValidSession so a
        // master-refreshed session is adopted instead of replaying a stale
        // refresh token (which can clear storage via invalid_grant).
        const shouldPreferStoredValidSession =
          options?.preferStoredValidSession ?? false;
        if (!forceRefresh || attempt > 0 || shouldPreferStoredValidSession) {
          const currentSession = await this.currentSession();
          if (this.isValidSession(currentSession)) {
            return currentSession as T;
          }
        }

        const refreshSource = (await this.currentSession()) ?? session;
        lastRefreshSource = refreshSource;
        const pkceForAttempt =
          await this.pkceManager.getCodeVerifierAndChallengesAsync();

        if (isPkceRefreshDebugEnabled()) {
          logPkceRefresh(`refresh attempt ${attempt + 1}/${maxRetries + 1}`, {
            sending: {
              codeVerifier: maskSecret(pkceForAttempt.codeVerifier),
              codeChallenge: maskSecret(pkceForAttempt.nextCodeChallenge),
              codeChallengeIsS256OfNext:
                pkceForAttempt.nextCodeChallenge ===
                (await this.pkceManager.getPkceChallenge(
                  pkceForAttempt.nextCodeVerifier
                )),
            },
          });
        }

        const refreshedSession = await this.refreshSession(
          refreshSource,
          pkceForAttempt.codeVerifier,
          pkceForAttempt.nextCodeChallenge
        );

        await this.pkceManager.updateCodeVerifierAsync();
        logPkceRefresh('refresh PKCE rotated (post oauth/token success)');

        const savedSession = await this.saveSession(refreshedSession);

        this.notifyObservers({
          type: SessionEventType.SESSION_REFRESHED,
          session: savedSession,
          previousSession: session,
        });

        return savedSession as T;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        logPkceRefreshFailure(`refresh attempt ${attempt + 1} failed`, {
          message: lastError.message,
          forceRefresh,
        });
        const failureType = this.classifyRefreshError(
          lastError,
          this.getRefreshErrorStatus(lastError)
        );

        if (failureType === 'permanent') {
          this.notifyObservers({
            type: SessionEventType.SESSION_REFRESH_FAILED,
            session,
            error: lastError,
            failureType: 'permanent',
            reason: 'refresh_token_invalid',
          });
          const recoveredSession = await this.currentSession();
          if (
            this.isValidSession(recoveredSession) &&
            this.hasSessionTokensChanged(lastRefreshSource, recoveredSession)
          ) {
            return recoveredSession as T;
          }
          await this.clearSession();
          throw new Error('Failed to refresh the session: token invalid');
        }

        if (attempt < maxRetries) {
          const delay = this.calculateRetryDelay(attempt);
          this.notifyObservers({
            type: SessionEventType.SESSION_REFRESH_RETRY,
            session,
            error: lastError,
            failureType: 'temporary',
            retryAttempt: attempt + 1,
            maxRetries,
            reason: `retry_${attempt + 1}_of_${maxRetries}`,
          });
          await sleep(delay);
        }
      }
    }

    this.notifyObservers({
      type: SessionEventType.SESSION_REFRESH_FAILED,
      session,
      error: lastError,
      failureType: 'temporary',
      reason: 'max_retries_exhausted',
    });

    const recoveredSession = await this.currentSession();
    if (
      this.isValidSession(recoveredSession) &&
      this.hasSessionTokensChanged(lastRefreshSource, recoveredSession)
    ) {
      return recoveredSession as T;
    }

    await this.clearSession();
    throw new Error('Failed to refresh the session: max retries exhausted');
  }

  private async currentSession(): Promise<ISession> {
    const session = await this.getSessionStorage().getItem(this.sessionKey);
    if (session) {
      return JSON.parse(session);
    } else {
      return {};
    }
  }

  private getSessionStorage(): IStorageAdapter {
    if (this.memoryOnly || this.sessionStorageTarget === 'LOCAL') {
      return this.storageAdapter;
    }

    if (this.sessionStorageTarget === 'SESSION' && this.sessionStorageAdapter) {
      return this.sessionStorageAdapter;
    }

    return this.storageAdapter;
  }

  private isValidConfiguration(configuration: IConfiguration): boolean {
    return Boolean(
      configuration.identityRedirectUri &&
        configuration.identityProvider &&
        configuration.identityClientApp &&
        configuration.identityClientId
    );
  }

  private convertToSession(grants: IAuthGrants): ISession {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy conversion from JWT claims
    const convertToUser = (data: Record<string, any>): IUser => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic property mapping
      const user: any = {};

      for (const key in data) {
        user[key] = data[key];
      }

      return user;
    };

    const claims = grants.id_token
      ? parseJwt<IIdentityToken>(grants.id_token)
      : undefined;

    return {
      expiresAt: (claims && claims.exp) || utcTimestamp() + 3600,
      refreshToken: grants.refresh_token,
      sessionToken: grants.id_token,
      accessToken: grants.access_token,
      user: claims && convertToUser(claims),
    };
  }

  private ensureConfigured(): void {
    if (!this.isValidConfiguration(this.config)) {
      throw new Error(
        'Invalid configuration. Did you forget to call configure()?'
      );
    }
  }

  private isValidSession(session: ISession | undefined): session is ISession {
    return (
      Boolean(
        session &&
          session.accessToken &&
          session.expiresAt &&
          session.expiresAt > utcTimestamp()
      ) && this.isValidSessionIssuer(session)
    );
  }

  private isValidSessionIssuer(session: ISession | undefined): boolean {
    return (
      !session?.sessionToken ||
      (!!session &&
        (!this.config ||
          !this.config.identityProvider ||
          !session.user ||
          !session.user.iss ||
          session.user.iss === this.config.identityProvider))
    );
  }

  private getRefreshErrorStatus(error: Error): number | undefined {
    const status = (error as Error & { status?: number }).status;
    return typeof status === 'number' ? status : undefined;
  }

  private hasSessionTokensChanged(
    before: ISession | undefined,
    after: ISession | undefined
  ): boolean {
    if (!after?.accessToken) {
      return false;
    }

    return (
      after.accessToken !== before?.accessToken ||
      after.refreshToken !== before?.refreshToken
    );
  }

  private async clearSession(): Promise<void> {
    const previousSession = await this.currentSession();
    await this.getSessionStorage().removeItem(this.sessionKey);

    this.notifyObservers({
      type: SessionEventType.SESSION_CLEARED,
      previousSession,
    });
  }

  private canRefresh(session: ISession | undefined): session is ISession {
    return Boolean(session && session.sessionToken && session.refreshToken);
  }

  private async requestSession(
    code: string,
    codeVerifier?: string,
    codeChallenge?: string
  ): Promise<ISession> {
    const res = await fetch(
      formatHref(this.config.identityProvider, '/oauth/token'),
      {
        body: formatQueryParameters({
          code,
          grant_type: 'authorization_code',
          redirect_uri: this.config.identityRedirectUri,
          tenantDomain: 'trimble.com',
          ...(codeVerifier
            ? {
                code_verifier: codeVerifier,
                code_challenge: codeChallenge,
                code_challenge_method: 'S256',
                client_id: this.config.identityClientId,
              }
            : {}),
        }),
        cache: 'default',
        headers: new Headers({
          ...(!codeVerifier
            ? {
                Accept: 'application/json',
                Authorization: `Basic ${btoa(
                  this.config.identityClientId +
                    ':' +
                    this.config.identityClientKey
                )}`,
              }
            : {}),
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
        method: 'POST',
      }
    );

    if (!res.ok) {
      throw new Error(res.statusText);
    }

    const grants = (await res.json()) as IAuthGrants;

    if (!grants.id_token) {
      throw new Error('Unauthorized');
    }

    return this.convertToSession(grants);
  }

  private async refreshSession(
    session: ISession,
    codeVerifier?: string,
    codeChallenge?: string
  ): Promise<ISession> {
    const res = await fetch(
      formatHref(this.config.identityProvider, '/oauth/token'),
      {
        body: formatQueryParameters({
          grant_type: 'refresh_token',
          refresh_token: session.refreshToken,
          tenantDomain: 'trimble.com',
          ...(codeVerifier
            ? {
                code_verifier: codeVerifier,
                code_challenge: codeChallenge,
                code_challenge_method: 'S256',
                client_id: this.config.identityClientId,
              }
            : {}),
        }),
        cache: 'default',
        headers: new Headers({
          ...(!codeVerifier
            ? {
                Accept: 'application/json',
                Authorization: `Basic ${btoa(
                  this.config.identityClientId +
                    ':' +
                    this.config.identityClientKey
                )}`,
              }
            : {}),
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
        method: 'POST',
      }
    );

    if (!res.ok) {
      const responseBody =
        typeof res.text === 'function' ? await res.text().catch(() => '') : '';
      const parsedBody = parseOAuthErrorBody(responseBody);
      logPkceRefreshFailure('oauth/token refresh_token failed', {
        status: res.status,
        statusText: res.statusText,
        correlationId: res.headers?.get('x-correlation-id') ?? null,
        identityProvider: this.config.identityProvider,
        codeVerifier: maskSecret(codeVerifier),
        codeChallenge: maskSecret(codeChallenge),
        responseBody,
        parsedBody,
      });
      const refreshError = new Error(
        responseBody ? `${res.statusText}: ${responseBody}` : res.statusText
      ) as Error & { status?: number };
      refreshError.status = res.status;
      throw refreshError;
    }

    const grants = (await res.json()) as IAuthGrants;

    if (!grants.id_token) {
      throw new Error('Unauthorized');
    }

    logPkceRefresh('oauth/token refresh_token succeeded', {
      status: res.status,
      correlationId: res.headers?.get('x-correlation-id') ?? null,
    });

    return this.convertToSession(grants);
  }

  private async revokeToken(
    token: string,
    tokenHint: string,
    codeVerifier?: string | null
  ): Promise<void> {
    const res = await fetch(
      formatHref(this.config.identityProvider, '/oauth/revoke'),
      {
        body: formatQueryParameters({
          token: token,
          token_type_hint: tokenHint,
          ...(codeVerifier
            ? {
                code_verifier: codeVerifier,
                client_id: this.config.identityClientId,
              }
            : {}),
        }),
        cache: 'default',
        headers: new Headers({
          ...(!codeVerifier
            ? {
                Authorization: `Basic ${btoa(
                  this.config.identityClientId +
                    ':' +
                    this.config.identityClientKey
                )}`,
              }
            : {}),
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
        method: 'POST',
      }
    );

    if (!res.ok) {
      throw new Error(res.statusText);
    }
  }

  private getSignInHref(
    states?: Record<string, string>,
    codeChallenge?: string
  ): string {
    const federatedIdentityProvider = parseQueryParameter(
      window.location.href,
      'identity_provider'
    );
    const returnUri = window.location.href.replace(
      /(?:code|sessionDataKey)(?:=[^&]*)?&?/g,
      ''
    );
    let stateUrl: string | undefined = returnUri;

    if (states) {
      const url = this.parseURL(returnUri);
      if (url) {
        const keys = Object.keys(states);
        keys.forEach((k) => url.searchParams.append(k, states[k]));
        stateUrl = url.href;
      }
    }

    if (stateUrl === this.config.identityRedirectUri) {
      stateUrl = undefined;
    }

    return formatHref(this.config.identityProvider, '/oauth/authorize', {
      client_id: this.config.identityClientId,
      redirect_uri: this.getRedirectUrl(),
      response_type: 'code',
      scope: this.buildScope(),
      state: stateUrl,
      identity_provider: federatedIdentityProvider,
      ...(codeChallenge
        ? { code_challenge: codeChallenge, code_challenge_method: 'S256' }
        : {}),
    });
  }

  private getSignOutHref(session: ISession): string {
    return formatHref(this.config.identityProvider, '/oauth/logout', {
      id_token_hint: session.sessionToken,
      post_logout_redirect_uri: this.getSignOutRedirectUrl(),
    });
  }

  private getRedirectUrl(): string {
    return this.config.identityRedirectUri;
  }

  private parseURL(url: string): URL | undefined {
    try {
      return new URL(url);
    } catch {
      return undefined;
    }
  }

  private notifyObservers(event: ISessionEvent): void {
    this.observerManager.notify(event);
  }

  private generateStateNonce(): string {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return (
      btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        // eslint-disable-next-line sonarjs/super-linear-regex -- anchored trailing-padding strip on bounded base64 output
        .replace(/=+$/, '')
    );
  }

  /**
   * Builds the OAuth `scope` string from the configured `identityClientApp`.
   *
   * `identityClientApp` may be `"openid"` (the minimum default used by
   * per-extension sessions) or any space-separated list of additional scopes.
   * We always ensure `openid` is present and deduplicate it so the resulting
   * string never contains `"openid openid"`.
   */
  private buildScope(): string {
    const app = (this.config.identityClientApp ?? '').trim();
    // TID requires at least one scope in addition to 'openid'.
    // Fall back to 'profile' when no application scope is configured.
    if (!app || app === 'openid') {
      return 'openid profile';
    }
    const parts = app.split(/\s+/);
    const deduped = ['openid', ...parts.filter((s) => s && s !== 'openid')];
    return deduped.join(' ');
  }
}
